import type {
  ComponentPropsWithoutRef,
  ElementType,
  KeyboardEvent,
  MouseEvent,
} from "react";
import { createElement, useCallback, useEffect, useRef } from "react";
import { useTypewriter } from "@/lib/hooks/useTypewriter";

type TypewriterTextProps<T extends ElementType> = {
  content: string;
  as?: T;
  charDelay?: number;
  startDelay?: number;
  enabled?: boolean;
  skipOnInteraction?: boolean;
  onComplete?: () => void;
} & Omit<ComponentPropsWithoutRef<T>, "children">;

export function TypewriterText<T extends ElementType = "span">(
  props: TypewriterTextProps<T>,
) {
  const {
    content,
    as,
    charDelay,
    startDelay,
    enabled,
    skipOnInteraction = true,
    onComplete,
    ...rest
  } = props;

  const Component = (as ?? "span") as T;
  const { text, isDone, skip } = useTypewriter(content, {
    charDelay,
    startDelay,
    enabled,
  });

  const lastDoneRef = useRef(isDone);
  useEffect(() => {
    if (isDone && !lastDoneRef.current) {
      onComplete?.();
    }
    lastDoneRef.current = isDone;
  }, [isDone, onComplete]);

  const handleSkip = useCallback(() => {
    if (!skipOnInteraction || isDone) {
      return;
    }
    skip();
  }, [isDone, skip, skipOnInteraction]);

  const { onClick, onKeyDown, ...restProps } =
    rest as ComponentPropsWithoutRef<T>;

  const handleClick = useCallback(
    (event: MouseEvent<Element>) => {
      onClick?.(event as never);
      if (event.defaultPrevented) {
        return;
      }
      handleSkip();
    },
    [handleSkip, onClick],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<Element>) => {
      onKeyDown?.(event as never);
      if (event.defaultPrevented) {
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        handleSkip();
      }
    },
    [handleSkip, onKeyDown],
  );

  return createElement(
    Component,
    {
      ...restProps,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      "aria-live": isDone ? "off" : "polite",
    },
    text,
  );
}
